import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User } from '../../../types';
import { createDateInZone, DEFAULT_TZ, formatTimeInZone } from '../../../shared/utils/timezone';
import { fromZonedTime } from 'date-fns-tz';
import { resolveEffectiveEventStaffing } from '../../squads/utils/effectiveEventStaffing';
import { eventHourlyRate } from '../../squads/utils/eventSupportingStaff';
import '../styles/PayrollCalculatorModal.css';

type PayrollRow = {
  teacherId: string;
  teacherName: string;
  teacherFirstName: string;
  teacherLastName: string;
  title: string;
  gustoEmployeeId: string;
  hourlyRate: number; // teacher's normal rate (User.hourlyRate)
  classCount: number;
  regularHours: number; // hours paid at the normal rate
  adminHours: number; // hours paid at a squad admin-pay override rate
  adminPay: number; // dollars earned at admin-pay rates
  adminRates: number[]; // distinct admin-pay rates applied
  totalHours: number;
  datesTaught: string[];
  totalPay: number;
};

type SquadPayInfo = {
  regularTeacher?: string;
  adminPayHourlyRate?: number | null;
  coTeachers: Array<{ teacherId?: string; adminPayHourlyRate?: number | null }>;
  staffPayByTeacherId: Record<string, number | null>;
};

// A rate override only counts when explicitly set to a number (0 is a valid override).
function overrideRate(v: unknown): number | null {
  if (v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseIsoDateParts(isoDate: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return { y, m: mo, d };
}

function getUtcRangeForInclusiveDates(startIso: string, endIso: string, timeZone: string): { startUtc: Date; endUtc: Date } | null {
  const s = parseIsoDateParts(startIso);
  const e = parseIsoDateParts(endIso);
  if (!s || !e) return null;

  const startLocal = new Date(s.y, s.m - 1, s.d, 0, 0, 0, 0);
  const endLocal = new Date(e.y, e.m - 1, e.d, 23, 59, 59, 999);
  const startUtc = fromZonedTime(startLocal, timeZone);
  const endUtc = fromZonedTime(endLocal, timeZone);
  if (Number.isNaN(startUtc.getTime()) || Number.isNaN(endUtc.getTime())) return null;
  return { startUtc, endUtc };
}

function money(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

function fmtHours(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  return v.toFixed(2);
}

function splitName(displayName: string): { firstName: string; lastName: string } {
  const raw = String(displayName || '').trim();
  if (!raw) return { firstName: '', lastName: '' };
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function deriveTitle(t: User | undefined | null): string {
  const subjects = t && Array.isArray(t.subjects) ? t.subjects : [];
  const set = new Set(subjects.map((s) => String(s).trim().toLowerCase()));
  const hasMusic = set.has('music');
  const hasAi = set.has('ai');
  if (hasAi && !hasMusic) return 'AI Teacher (Primary)';
  if (hasMusic && !hasAi) return 'Music Teacher (Primary)';
  if (hasMusic && hasAi) return 'Music & AI Teacher (Primary)';
  return 'Teacher (Primary)';
}

function csvEscape(v: unknown): string {
  const s = String(v ?? '');
  // Wrap everything to keep it simple and safe.
  return `"${s.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface PayrollCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  teachers: User[];
}

const PayrollCalculatorModal: React.FC<PayrollCalculatorModalProps> = ({ isOpen, onClose, teachers }) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [rows, setRows] = useState<PayrollRow[] | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string>('');

  const teacherById = useMemo(() => {
    const m = new Map<string, User>();
    (teachers || []).forEach(t => {
      if (t?.id) m.set(t.id, t);
    });
    return m;
  }, [teachers]);

  // Default to a biweekly window ending "today" (in CT) whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const tz = DEFAULT_TZ;
    const endIso = formatTimeInZone(new Date(), tz, 'yyyy-MM-dd');
    const endNoonUtc = createDateInZone(endIso, '12:00', tz);
    const startNoonUtc = new Date(endNoonUtc.getTime());
    startNoonUtc.setUTCDate(startNoonUtc.getUTCDate() - 13); // inclusive biweekly (14 days)
    const startIso = formatTimeInZone(startNoonUtc, tz, 'yyyy-MM-dd');

    setEndDate(endIso);
    setStartDate(startIso);
    setRows(null);
    setError('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setError('');
    setRows(null);
    onClose();
  };

  const handleCalculate = async () => {
    setError('');
    setIsCalculating(true);
    try {
      const tz = DEFAULT_TZ;
      const range = getUtcRangeForInclusiveDates(startDate, endDate, tz);
      if (!range) {
        setError('Please select a valid start and end date.');
        return;
      }
      const { startUtc, endUtc } = range;
      if (startUtc.getTime() > endUtc.getTime()) {
        setError('Start date must be on or before end date.');
        return;
      }

      const [snap, squadsSnap] = await Promise.all([
        getDocs(collection(db, 'squadEvents')),
        getDocs(collection(db, 'squads')),
      ]);

      const squadById = new Map<string, SquadPayInfo>();
      squadsSnap.docs.forEach(d => {
        const s: any = d.data();
        squadById.set(d.id, {
          regularTeacher: typeof s?.regularTeacher === 'string' ? s.regularTeacher : undefined,
          adminPayHourlyRate: s?.adminPayHourlyRate,
          coTeachers: Array.isArray(s?.coTeachers) ? s.coTeachers : [],
          staffPayByTeacherId: (s?.staffPayByTeacherId && typeof s.staffPayByTeacherId === 'object') ? s.staffPayByTeacherId : {},
        });
      });

      const acc = new Map<string, PayrollRow>();

      snap.docs.forEach(d => {
        const data: any = d.data();
        const start: Date | null = data?.start?.toDate ? data.start.toDate() : null;
        const end: Date | null = data?.end?.toDate ? data.end.toDate() : null;
        if (!start || !end) return;
        const startMs = start.getTime();
        if (Number.isNaN(startMs)) return;
        if (start < startUtc || start > endUtc) return;

        const teacherIds = new Set<string>();
        const effectiveStaffing = resolveEffectiveEventStaffing(data);
        if (effectiveStaffing.effectiveLeadTeacherId) teacherIds.add(effectiveStaffing.effectiveLeadTeacherId);
        effectiveStaffing.effectiveSupportingTeacherIds.forEach(id => teacherIds.add(id));
        if (teacherIds.size === 0) return;

        const durationHours = Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
        const taughtDate = formatTimeInZone(start, tz, 'yyyy-MM-dd');
        const squad = squadById.get(String(data?.squadId || ''));

        teacherIds.forEach(teacherId => {
          const t = teacherById.get(teacherId);
          const teacherName = (t?.displayName || teacherId).trim();
          const { firstName, lastName } = splitName(teacherName);
          const title = deriveTitle(t);
          const gustoEmployeeId = String((t as any)?.gustoEmployeeId || '').trim();
          const hourlyRate = Number.isFinite(t?.hourlyRate) ? Number(t?.hourlyRate) : 0;

          // Squad admin-pay overrides, in precedence order: a co-teacher entry's
          // rate wins for that teacher; then a per-staff-member rate
          // (staffPayByTeacherId); then the squad-level rate for whoever leads
          // the class. Anyone else gets their normal rate.
          // A date-specific rate takes precedence and belongs to this person only.
          let adminRate: number | null = effectiveStaffing.effectiveSupportingTeacherIds.includes(teacherId)
            ? eventHourlyRate(data.supportingStaff, teacherId) : null;
          if (adminRate === null && squad) {
            const co = squad.coTeachers.find(c => String(c?.teacherId || '') === teacherId);
            const coRate = co ? overrideRate(co.adminPayHourlyRate) : null;
            const staffRate = overrideRate(squad.staffPayByTeacherId[teacherId]);
            if (coRate !== null) {
              adminRate = coRate;
            } else if (staffRate !== null) {
              adminRate = staffRate;
            } else {
              const leadRate = overrideRate(squad.adminPayHourlyRate);
              const isLead = teacherId === String(effectiveStaffing.effectiveLeadTeacherId || '')
                || teacherId === String(squad.regularTeacher || '');
              if (leadRate !== null && isLead) adminRate = leadRate;
            }
          }
          const effectiveRate = adminRate !== null ? adminRate : hourlyRate;
          const pay = effectiveRate * durationHours;

          let row = acc.get(teacherId);
          if (!row) {
            row = {
              teacherId,
              teacherName,
              teacherFirstName: firstName,
              teacherLastName: lastName,
              title,
              gustoEmployeeId,
              hourlyRate,
              classCount: 0,
              regularHours: 0,
              adminHours: 0,
              adminPay: 0,
              adminRates: [],
              totalHours: 0,
              datesTaught: [],
              totalPay: 0,
            };
            acc.set(teacherId, row);
          }

          row.classCount += 1;
          row.totalHours += durationHours;
          row.totalPay += pay;
          row.datesTaught.push(taughtDate);
          if (adminRate !== null) {
            row.adminHours += durationHours;
            row.adminPay += pay;
            if (!row.adminRates.includes(adminRate)) row.adminRates.push(adminRate);
          } else {
            row.regularHours += durationHours;
          }
        });
      });

      const out = Array.from(acc.values())
        .map(r => {
          const uniqueDates = Array.from(new Set(r.datesTaught)).sort();
          return { ...r, datesTaught: uniqueDates };
        })
        .sort((a, b) => a.teacherName.localeCompare(b.teacherName));

      setRows(out);
    } catch (e: any) {
      console.error('Payroll calculation failed:', e);
      setError('Failed to calculate payroll. Check console for details.');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleExport = () => {
    if (!rows) return;
    // Gusto timesheet import format (keep header order).
    const header = [
      'last_name',
      'first_name',
      'title',
      'gusto_employee_id',
      'regular_hours',
      'overtime_hours',
      'double_overtime_hours',
      'missed_break_hours',
      'bonus',
      'commission',
      'paycheck_tips',
      'cash_tips',
      'correction_payment',
      'reimbursement',
      'personal_note',
    ];
    const lines = [
      header.map(csvEscape).join(','),
      ...rows.map(r => {
        // Admin-pay hours are excluded from regular_hours (Gusto would pay them at the
        // teacher's normal Gusto rate) and exported as a flat correction_payment instead.
        const regularHours = (Number.isFinite(r.regularHours) ? r.regularHours : 0).toFixed(1);
        const hasAdmin = r.adminHours > 0;
        const correctionPayment = hasAdmin && r.adminPay > 0 ? r.adminPay.toFixed(2) : '';
        const personalNote = hasAdmin
          ? `Admin pay: ${r.adminHours.toFixed(1)} hrs at ${r.adminRates.map(x => `$${x.toFixed(2)}/hr`).join(', ')} = $${r.adminPay.toFixed(2)}`
          : '';
        return ([
          r.teacherLastName,
          r.teacherFirstName,
          r.title,
          r.gustoEmployeeId,
          regularHours,
          '', // overtime_hours
          '', // double_overtime_hours
          '', // missed_break_hours
          '', // bonus
          '', // commission
          '', // paycheck_tips
          '', // cash_tips
          correctionPayment,
          '', // reimbursement
          personalNote,
        ]).map(csvEscape).join(',');
      }),
    ];
    const tz = DEFAULT_TZ;
    const endIso = endDate || formatTimeInZone(new Date(), tz, 'yyyy-MM-dd');
    downloadCsv(`overture-games-inc-timesheet-${endIso}.csv`, lines.join('\n'));
  };

  return (
    <div className="payroll-modal-overlay" onClick={handleClose}>
      <div className="payroll-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payroll-modal-header">
          <h2>Calculate Payroll</h2>
          <button className="payroll-close-btn" onClick={handleClose} aria-label="Close">×</button>
        </div>

        <div className="payroll-modal-subtitle">
          Uses scheduled class events from <code>squadEvents</code>. Dates are interpreted in <strong>Central Time (CT)</strong>.
          Squads with an admin pay rate (or per-co-teacher rates) override each teacher's normal hourly rate; in the
          CSV those hours are exported as a <code>correction_payment</code> amount instead of <code>regular_hours</code>.
        </div>

        <div className="payroll-controls">
          <div className="payroll-field">
            <label htmlFor="payroll-start">Start date (inclusive)</label>
            <input
              id="payroll-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="payroll-field">
            <label htmlFor="payroll-end">End date (inclusive)</label>
            <input
              id="payroll-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="payroll-actions">
            <button className="payroll-cancel-btn" onClick={handleClose}>Close</button>
            <button className="payroll-calc-btn" onClick={handleCalculate} disabled={isCalculating}>
              {isCalculating ? '⏳ Calculating…' : 'Calculate'}
            </button>
          </div>
        </div>

        {error && <div className="payroll-error">{error}</div>}

        {rows && (
          <div className="payroll-results">
            <div className="payroll-results-toolbar">
              <div className="payroll-results-meta">
                <strong>{rows.length}</strong> teacher{rows.length === 1 ? '' : 's'} found
              </div>
              <button className="payroll-export-btn" onClick={handleExport} disabled={rows.length === 0}>
                Export CSV
              </button>
            </div>

            <div className="payroll-table-wrapper">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Teacher</th>
                    <th>Hourly Rate</th>
                    <th>Hours Worked</th>
                    <th># Classes</th>
                    <th>Dates Taught</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="payroll-empty">
                        No classes found in this date range.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={r.teacherId} className={idx % 2 === 0 ? 'even' : 'odd'}>
                        <td className="sticky-col">{r.teacherName}</td>
                        <td>
                          {money(r.hourlyRate)}
                          {r.adminHours > 0 && (
                            <div style={{ fontSize: 12, opacity: 0.75 }}>
                              Admin: {r.adminRates.map(money).join(', ')}/hr
                            </div>
                          )}
                        </td>
                        <td>
                          {fmtHours(r.totalHours)}
                          {r.adminHours > 0 && (
                            <div style={{ fontSize: 12, opacity: 0.75 }}>
                              {fmtHours(r.adminHours)} admin
                            </div>
                          )}
                        </td>
                        <td>{r.classCount}</td>
                        <td className="wrap">{r.datesTaught.join(', ')}</td>
                        <td><strong>{money(r.totalPay)}</strong></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollCalculatorModal;
